import clsx from "clsx";
import { EmailInput } from "@/components/EmailInput";
import { LicensePlateInput } from "@/components/LicensePlateInput";
import { PhoneInput } from "@/components/PhoneInput";
import { CountrySelect } from "@/components/CountrySelect";
import { BirthDateInput } from "@/components/BirthDateInput";
import { SalutationSelect } from "@/components/SalutationSelect";
import { StreetHouseNumberFields } from "@/components/StreetHouseNumberFields";
import type {
  CompanionFieldKey,
  CompanionFieldsConfig,
  CompanionFieldsConfigByRole,
  CompanionFormData,
} from "@/lib/companionFields";
import {
  COMPANION_FIELD_KEYS,
  companionFieldConfigForRole,
  companionSlotLabel,
  getCompanionFieldLabels,
} from "@/lib/companionFields";
import { useLocale } from "@/i18n";
import { companionFromKnown, knownCompanionLabel, type KnownCompanion } from "@/lib/knownCompanions";
import { isDeprecatedAgeField } from "@/lib/birthDateAge";
import type { GooglePlacesAddressPatch } from "@/lib/googlePlaces";
import { useEffect, useRef } from "react";

type Props = {
  companions: CompanionFormData[];
  configs: CompanionFieldsConfigByRole;
  onChange: (companions: CompanionFormData[]) => void;
  onBirthDateBlur?: (companions: CompanionFormData[]) => void;
  onBirthDateEntryError?: (message: string | null) => void;
  knownCompanions?: KnownCompanion[];
  googlePlacesApiKey?: string | null;
  /** Leere Pflichtfelder nach einem Weiter-Klick markieren. */
  highlightMissing?: boolean;
};

function isMissingValue(highlight: boolean, required: boolean, value: string): boolean {
  return highlight && required && value.trim().length === 0;
}

function updateCompanion(
  companions: CompanionFormData[],
  index: number,
  patch: Partial<CompanionFormData>,
): CompanionFormData[] {
  return companions.map((c, i) => (i === index ? { ...c, ...patch } : c));
}

function activeFields(config: CompanionFieldsConfig): CompanionFieldKey[] {
  return COMPANION_FIELD_KEYS.filter((k) => config[k] !== "off" && !isDeprecatedAgeField(k));
}

function usesStreetHouseRow(fields: CompanionFieldKey[], index: number): boolean {
  return fields[index] === "street" && fields[index + 1] === "house_number";
}

export function CompanionFieldsForm({
  companions,
  configs,
  onChange,
  onBirthDateBlur,
  onBirthDateEntryError,
  knownCompanions,
  googlePlacesApiKey,
  highlightMissing = false,
}: Props) {
  const { messages } = useLocale();
  const birthDateErrors = useRef<Record<number, string | null>>({});

  useEffect(() => {
    const next: Record<number, string | null> = {};
    companions.forEach((_, index) => {
      next[index] = birthDateErrors.current[index] ?? null;
    });
    birthDateErrors.current = next;
    const first = Object.values(next).find((item) => item) ?? null;
    onBirthDateEntryError?.(first);
  }, [companions, onBirthDateEntryError]);
  const fieldLabels = getCompanionFieldLabels(messages);
  const hasVisibleFields = companions.some(
    (companion) => activeFields(companionFieldConfigForRole(configs, companion.role)).length > 0,
  );
  if (companions.length === 0 || !hasVisibleFields) return null;

  let adultIndex = 0;
  let childIndex = 0;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="bk-section-title">{messages.flow.companionsSummary}</h3>
        <p className="bk-section-hint">
          {messages.flow.companionDetailsHint}
          {knownCompanions && knownCompanions.length > 0
            ? ` ${messages.flow.knownCompanionHint}`
            : null}
        </p>
      </div>
      {companions.map((companion, index) => {
        const config = companionFieldConfigForRole(configs, companion.role);
        const fields = activeFields(config);
        if (fields.length === 0) return null;
        const slotIndex = companion.role === "child" ? childIndex++ : adultIndex++;
        const title = companionSlotLabel(companion.role, slotIndex, messages);
        const selectable = knownCompanions?.filter((k) => k.role === companion.role) ?? [];
        return (
          <fieldset
            key={`${companion.role}-${index}`}
            className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4"
          >
            <legend className="px-1 text-sm font-semibold text-slate-900">{title}</legend>
            {selectable.length > 0 ? (
              <label className="block text-sm font-medium text-slate-700">
                {messages.flow.knownCompanionSelect}
                <select
                  className="bk-input mt-1"
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    const known = selectable.find((k) => k.id === id);
                    if (!known) return;
                    const updated = updateCompanion(
                      companions,
                      index,
                      companionFromKnown(known, companion.role),
                    );
                    onChange(updated);
                    if (known.date_of_birth?.trim()) {
                      onBirthDateBlur?.(updated);
                    }
                  }}
                >
                  <option value="">{messages.flow.knownCompanionManual} …</option>
                  {selectable.map((k) => (
                    <option key={k.id} value={k.id}>
                      {knownCompanionLabel(k)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {fields.map((key, fieldIndex) => {
                if (usesStreetHouseRow(fields, fieldIndex)) {
                  return (
                    <StreetHouseNumberFields
                      key="street-house_number"
                      values={{
                        street: companion.street,
                        house_number: companion.house_number,
                      }}
                      streetRequired={config.street === "required"}
                      houseNumberRequired={config.house_number === "required"}
                      streetMissing={isMissingValue(
                        highlightMissing,
                        config.street === "required",
                        companion.street,
                      )}
                      houseNumberMissing={isMissingValue(
                        highlightMissing,
                        config.house_number === "required",
                        companion.house_number,
                      )}
                      onChange={(patch) =>
                        onChange(updateCompanion(companions, index, patch))
                      }
                      googlePlacesApiKey={googlePlacesApiKey}
                      onAddressAutocomplete={(patch: GooglePlacesAddressPatch) =>
                        onChange(updateCompanion(companions, index, patch))
                      }
                    />
                  );
                }
                if (
                  key === "house_number" &&
                  fieldIndex > 0 &&
                  fields[fieldIndex - 1] === "street"
                ) {
                  return null;
                }

                if (key === "street" && !usesStreetHouseRow(fields, fieldIndex)) {
                  return (
                    <StreetHouseNumberFields
                      key="street-only"
                      values={{
                        street: companion.street,
                        house_number: companion.house_number,
                      }}
                      streetRequired={config.street === "required"}
                      houseNumberRequired={false}
                      streetMissing={isMissingValue(
                        highlightMissing,
                        config.street === "required",
                        companion.street,
                      )}
                      showHouseNumber={false}
                      onChange={(patch) =>
                        onChange(updateCompanion(companions, index, patch))
                      }
                      googlePlacesApiKey={googlePlacesApiKey}
                      onAddressAutocomplete={(patch: GooglePlacesAddressPatch) =>
                        onChange(updateCompanion(companions, index, patch))
                      }
                    />
                  );
                }

                const required = config[key] === "required";
                const label = `${fieldLabels[key]}${required ? " *" : ""}`;
                const missing = isMissingValue(highlightMissing, required, companion[key]);
                if (key === "date_of_birth") {
                  return (
                    <div
                      key={key}
                      className="sm:col-span-2"
                      data-missing-field={missing ? "true" : undefined}
                    >
                      <BirthDateInput
                        variant="booking"
                        legend={label}
                        value={companion.date_of_birth}
                        copy={messages.birthDate}
                        required={required}
                        missing={missing}
                        onChange={(date_of_birth) =>
                          onChange(updateCompanion(companions, index, { date_of_birth }))
                        }
                        onBlur={(date_of_birth) =>
                          onBirthDateBlur?.(
                            updateCompanion(companions, index, { date_of_birth }),
                          )
                        }
                        onErrorChange={(message) => {
                          birthDateErrors.current[index] = message;
                          const first =
                            companions
                              .map((_, companionIndex) => birthDateErrors.current[companionIndex])
                              .find((item) => item) ?? null;
                          onBirthDateEntryError?.(first);
                        }}
                      />
                    </div>
                  );
                }
                return (
                  <label
                    key={key}
                    className="text-sm font-medium"
                    data-missing-field={missing ? "true" : undefined}
                  >
                    {label}
                    {key === "title" ? (
                      <SalutationSelect
                        value={companion.title}
                        onChange={(title) =>
                          onChange(updateCompanion(companions, index, { title }))
                        }
                        required={required}
                        missing={missing}
                      />
                    ) : key === "email" ? (
                      <EmailInput
                        value={companion.email}
                        onChange={(email) =>
                          onChange(updateCompanion(companions, index, { email }))
                        }
                        required={required}
                        missing={missing}
                      />
                    ) : key === "phone" ? (
                      <PhoneInput
                        value={companion.phone}
                        onChange={(phone) =>
                          onChange(updateCompanion(companions, index, { phone }))
                        }
                        required={required}
                        missing={missing}
                      />
                    ) : key === "license_plate" ? (
                      <LicensePlateInput
                        value={companion.license_plate}
                        onChange={(license_plate) =>
                          onChange(updateCompanion(companions, index, { license_plate }))
                        }
                        required={required}
                        missing={missing}
                      />
                    ) : key === "country_code" ? (
                      <CountrySelect
                        value={companion.country_code}
                        onChange={(country_code) =>
                          onChange(updateCompanion(companions, index, { country_code }))
                        }
                        required={required}
                        missing={missing}
                        autoComplete="country"
                      />
                    ) : (
                      <input
                        type="text"
                        className={clsx("bk-input mt-1", missing && "bk-input--missing")}
                        value={companion[key]}
                        onChange={(e) =>
                          onChange(updateCompanion(companions, index, { [key]: e.target.value }))
                        }
                        data-address-field={key}
                        aria-invalid={missing || undefined}
                        required={required}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
